import { apiKeyManager } from '@empiriecom/mybuy-session/ApiKey';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { CheckoutOrderableBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/CheckoutOrderableBasketFragmentFragmentDoc';
import { UpdatePreparedBasketDocument } from '@packages/gql/generated/shopping/UpdatePreparedBasketDocument';
import type { CheckoutPaymentSettingsInput } from '@packages/gql/generated/shopping/graphql';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { csvOrUndefined, storedLoyaltyCoupons } from './usePreparedBasket';

/**
 * Contract note for future checkout payment subflows.
 *
 * Null-safe policy:
 * - Omit fields that are not applicable/visible for the selected payment method.
 * - Keep base `paymentType` submit working even when optional payment flags are missing from runtime basket.
 *
 * Expected CheckoutPaymentSettingsInput payload by subflow:
 * - Base selection: { paymentType }
 * - Direct debit / SEPA: { paymentType, directDebitSelected? }
 * - Delayed payment / Valuta: { paymentType, delayedPayment?, delayedPaymentAllowed? }
 * - Insurance: { paymentType, insuranceSelected?, insuranceAllowed? }
 * - Installments (incl. salary-deduction installments): { paymentType, installmentCount? }
 * - Salary deduction base: { paymentType }
 * - Down-payment hint passthrough (if backend expects it): { paymentType, downPaymentHint? }
 */
export type CheckoutPaymentPayload = CheckoutPaymentSettingsInput;

export const UpdatePreparedBasketMutation = /* GraphQL */ `
  mutation UpdatePreparedBasket(
    $locale: String!
    $page: CheckoutTargetPage!
    $changeOrderableBasketInput: CheckoutChangeOrderableBasketInput
    $coupons: String
    $loyaltyCoupons: String
    $addHints: String
    $fp: String
  ) {
    updatePreparedBasket(
      locale: $locale
      page: $page
      changeOrderableBasketInput: $changeOrderableBasketInput
      coupons: $coupons
      loyaltyCoupons: $loyaltyCoupons
      addHints: $addHints
      fp: $fp
    ) {
      __typename
      ... on CheckoutOrderableBasket {
        ...CheckoutOrderableBasketFragment
      }
      ... on CheckoutServerFailure {
        failureMessage
        failureType
      }
    }
  }
`;

export const useUpdatePreparedBasket = () => {
  const [state, rawExecute] = useAuthMutation(UpdatePreparedBasketDocument);

  const unmaskBasket = (raw: NonNullable<typeof state.data>['updatePreparedBasket'] | undefined) =>
    raw?.__typename === 'CheckoutOrderableBasket'
      ? {
          __typename: 'CheckoutOrderableBasket' as const,
          ...getFragmentData(CheckoutOrderableBasketFragmentFragmentDoc, raw),
        }
      : raw;

  const stateRaw = state.data?.updatePreparedBasket;
  const data = state.data ? { updatePreparedBasket: unmaskBasket(stateRaw) } : state.data;

  const execute = async (...args: Parameters<typeof rawExecute>) => {
    const [variables, context] = args;
    // The checkout BFF is stateless about coupons/loyalty codes - each caller only ever
    // touches one of the two (useCoupons sets `coupons`, useJoeCode sets `loyaltyCoupons`),
    // so the field it omits must fall back to the durable store instead of being sent as
    // `undefined`, which would otherwise wipe out the other code on every update. A caller
    // that does set the key - including explicitly to `undefined`, to clear it - always wins.
    const mergedVariables = {
      ...variables,
      coupons:
        'coupons' in variables ? variables.coupons : csvOrUndefined(apiKeyManager.getBonuscodes()),
      loyaltyCoupons:
        'loyaltyCoupons' in variables ? variables.loyaltyCoupons : storedLoyaltyCoupons(),
    };

    const result = await rawExecute(mergedVariables, context);
    const resultRaw = result.data?.updatePreparedBasket;
    return {
      ...result,
      data: result.data ? { updatePreparedBasket: unmaskBasket(resultRaw) } : result.data,
    };
  };

  return [{ ...state, data }, execute] as const;
};
